import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Radio, RadioGroup, TextInput } from '@cfa/react-core';

import PathwayModal from '@/components/popups/PathwayModal';

export interface AddQuizOwnerOption {
  /** Operator id (V1) or auth `ownerId` (V2). */
  id: string;
  label: string;
}

interface AddQuizPopUpProps {
  isLicenseeUser: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  options: AddQuizOwnerOption[];
  submitHandler: ({
    selectedId,
    quizNameInput,
  }: {
    selectedId?: string;
    quizNameInput: string;
  }) => void;
}

const STEP = {
  OPERATOR: 'operator',
  NAME: 'name',
} as const;

type StepKey = (typeof STEP)[keyof typeof STEP];

const AddQuizPopUp: React.FC<AddQuizPopUpProps> = ({
  isLicenseeUser,
  open,
  setOpen,
  options,
  submitHandler,
}) => {
  const { t } = useTranslation();
  const shouldSelectOption = options.length > 1 && !isLicenseeUser;
  const initialStep: StepKey = shouldSelectOption ? STEP.OPERATOR : STEP.NAME;
  const [selectedId, setSelectedId] = useState<string>();
  const [step, setStep] = useState<StepKey>(initialStep);
  const [quizNameInput, setQuizNameInput] = useState('');

  const effectiveSelectedId =
    !shouldSelectOption && !selectedId && options.length === 1
      ? options[0]?.id
      : selectedId;

  const isCreateDisabled = quizNameInput.trim().length === 0;

  useEffect(() => {
    if (shouldSelectOption) {
      setSelectedId(undefined);
    }
    setStep(initialStep);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialStep, open]);

  const closePopup = () => {
    setStep(initialStep);
    setOpen(false);
  };

  const createQuiz = () =>
    submitHandler({ selectedId: effectiveSelectedId, quizNameInput });

  return (
    <PathwayModal
      className="2xl:max-w-[610px]!"
      isOpen={open}
      onClose={closePopup}
      stepKey={step}
    >
      <PathwayModal.Step stepKey={STEP.OPERATOR}>
        <PathwayModal.Header>
          <PathwayModal.Title>
            {t('TrainingPlans.buildQuizzes.chooseOperator')}
          </PathwayModal.Title>
        </PathwayModal.Header>
        <PathwayModal.Body noMinHeight>
          <div className="flex justify-center">
            <RadioGroup
              name="operatorList"
              orientation="vertical"
              value={selectedId ?? ''}
              onChange={setSelectedId}
            >
              {options.map(option => (
                <Radio key={option.id} value={option.id}>
                  {option.label}
                </Radio>
              ))}
            </RadioGroup>
          </div>
        </PathwayModal.Body>
        <PathwayModal.Footer>
          <PathwayModal.SecondaryButton
            onPress={closePopup}
            testId="PrimaryConfirmButton"
          >
            {t('Button.cancel')}
          </PathwayModal.SecondaryButton>
          <PathwayModal.PrimaryButton
            color="secondary"
            isDisabled={!selectedId}
            onPress={() => setStep(STEP.NAME)}
            testId="SecondaryConfirmButton"
            variant="filled"
          >
            {t('Button.next')}
          </PathwayModal.PrimaryButton>
        </PathwayModal.Footer>
      </PathwayModal.Step>

      <PathwayModal.Step stepKey={STEP.NAME}>
        <PathwayModal.Header>
          <PathwayModal.Title>
            {t('Generic.giveName', { object: t('Generic.quiz') })}
          </PathwayModal.Title>
        </PathwayModal.Header>
        <PathwayModal.Body noMinHeight>
          <TextInput
            data-testid="InputQuizName"
            onChange={setQuizNameInput}
            onKeyDown={e =>
              e.key === 'Enter' && !isCreateDisabled && createQuiz()
            }
            placeholder={t('TrainingPlans.buildQuizzes.quizNamePlaceholder')}
            value={quizNameInput}
            maxLength={250}
          />
        </PathwayModal.Body>
        <PathwayModal.Footer>
          <PathwayModal.SecondaryButton
            onPress={
              shouldSelectOption ? () => setStep(STEP.OPERATOR) : closePopup
            }
            testId="PrimaryConfirmButton"
          >
            {shouldSelectOption ? t('Button.back') : t('Button.cancel')}
          </PathwayModal.SecondaryButton>
          <PathwayModal.PrimaryButton
            color="secondary"
            isDisabled={isCreateDisabled}
            onPress={createQuiz}
            testId="SecondaryConfirmButton"
            variant="filled"
          >
            {t('Button.create')}
          </PathwayModal.PrimaryButton>
        </PathwayModal.Footer>
      </PathwayModal.Step>
    </PathwayModal>
  );
};

export default AddQuizPopUp;
